/**
 * Why a business customer was taken off the missing-VAT-ID worklist (CLR-276).
 * Mirrors the backend `VatIdExclusionReason` enum — the values are sent as-is.
 */
export const VAT_ID_EXCLUSION_REASONS = [
  {
    value: "SMALL_BUSINESS_19_USTG",
    label: "Small business (§ 19 UStG)",
    hint: "Kleinunternehmer — never issued a VAT ID.",
  },
  {
    value: "NOT_VAT_REGISTERED",
    label: "Not VAT-registered",
    hint: "The business exists but is not registered for VAT.",
  },
  {
    value: "OTHER",
    label: "Other",
    hint: "Needs a note so the entry still makes sense later.",
  },
] as const;

export type VatIdExclusionReason =
  (typeof VAT_ID_EXCLUSION_REASONS)[number]["value"];

export interface VatIdExclusion {
  reason: VatIdExclusionReason;
  note: string | null;
  excluded_by: string;
  excluded_at: string;
}

/** Falls back to the raw value so an unknown reason is still readable. */
export const reasonLabel = (reason: string): string =>
  VAT_ID_EXCLUSION_REASONS.find((r) => r.value === reason)?.label ?? reason;

/** The backend rejects OTHER without a note, so the form must too. */
export const reasonRequiresNote = (reason: string | null): boolean =>
  reason === "OTHER";

export const MAX_NOTE_LENGTH = 500;

/**
 * Shared so the Clearing worklist and the customer details page render the same
 * timestamp — the app's generic formatDate uses the browser locale, which would
 * make the two screens disagree.
 */
export const formatExclusionTimestamp = (value?: string | null): string =>
  value ? new Date(value).toLocaleString("de-DE") : "—";

/** Label for a customer's place on the missing-VAT-ID list, shared by the details page and the list. */
export const NOT_EXCLUDED_LABEL = "Not excluded";

export const exclusionStatusLabel = (
  exclusion?: Pick<VatIdExclusion, "reason"> | null,
): string => (exclusion ? reasonLabel(exclusion.reason) : NOT_EXCLUDED_LABEL);

/**
 * The VAT the customer's next credit note will carry, decided by the backend with the
 * same rule credit-note generation uses (CLR-273). Never re-derive it from `vat_id`:
 * a German business without a VAT ID still gets 19 % unless it is excluded.
 */
export interface CreditNoteVat {
  treatment: string;
  vat_percent: number;
  includes_vat: boolean;
}

export const creditNoteVatLabel = (vat?: CreditNoteVat | null): string => {
  if (!vat) return "—";
  return vat.includes_vat
    ? `With VAT (${vat.vat_percent} %)`
    : "Without VAT (0 %)";
};
